
import { Component, Vue, Watch, Prop } from "vue-property-decorator";
import ContainerFrame from "@/components/ContainerFrame.vue";
import StationLocationForm from "@/components/stationDetails/StationLocationForm.vue";
import DeviceSettings from "@/components/stationDetails/DeviceSettings.vue";
import TypeDescriptions from "@/components/stationDetails/TypeDescriptions.vue";
import PropertyDescriptions from "@/components/stationDetails/PropertyDescriptions.vue";
import StationPricing from "@/components/stationDetails/StationPricing.vue";
import {
  DeviceDimensions,
  Station,
  StationLocation,
  Device,
  RentalMethod,
  DeviceTypeSettings,
  DeviceTypeDescription,
  DevicePropertyDescription,
  Pricing,
} from "@/api/dtos/Station.schema";
import { getDefaultDevicePricing } from "@/misc/defaults";
import api from "@/api";
import { showToast } from "@/utils/toastService";
import { getAxiosResponse } from "@/utils/errorUtils";
import { DeviceHardwareSettings } from "@/api/dtos/DeviceHardwareSettings.schema";

@Component({
  components: {
    ContainerFrame,
    StationLocationForm,
    DeviceSettings,
    TypeDescriptions,
    PropertyDescriptions,
    StationPricing,
  },
})
export default class NewStationView extends Vue {
  @Prop({
    default: () => {
      return {
        uuid: "",
        isActive: false,
        platformServiceActive: false,
        devicePropertyDescriptions: [],
        deviceTypeDescriptions: [],
        devices: [],
        contactData: {
          serviceEmail: "",
          servicePhone: "",
        },
        stationLocation: {
          address: {
            city: "",
            country: "DE",
            postalCode: "",
            street: "",
          },
          lat: 0,
          lon: 0,
          stationName: "",
        },
        pricingModels: [],
        operator: {
          name: "",
          uuid: "",
        },
        serviceCards: [],
      };
    },
  })
  station!: Station;

  @Prop({ required: true })
  operatorId!: string;

  step = 1;
  locationValid = false;
  descriptionsValid = false;
  propertiesValid = false;
  imageValid = false;
  pricesValid = true;
  countries = require("@/misc/countries_german.json");
  isTaxable = false;

  hasFinanceData = false;

  isWaitingForResponse = false;

  async mounted(): Promise<void> {
    this.station.operator.uuid = this.operatorId;

    try {
      const operator = await api.OperatorsApi.getOperator(this.operatorId);
      this.hasFinanceData = !!operator.finance;
    } catch (e) {
      console.error("Could not load operator", e);
      this.hasFinanceData = false;
    }

    this.onDevicesChanged();
  }

  async createStation(): Promise<void> {
    this.isWaitingForResponse = true;
    try {
      const station = await api.StationsApi.createStation(this.station);
      showToast({
        message: `Station erfolgreich erstellt`,
        type: "success",
      });
      this.$router.push({
        name: "Stationsdetails",
        params: { stationUuid: station.uuid },
      });
    } catch (e) {
      showToast({
        response: getAxiosResponse(e),
        message: `Station konnte nicht erstellt werden`,
        type: "success",
      });
      console.log("Create new station ERROR", e);
    } finally {
      this.isWaitingForResponse = false;
    }
  }

  @Watch("station.devices", {
    deep: true,
  })
  onDevicesChanged(): void {
    const allDeviceTypesInStation = [
      ...new Set(this.station?.devices.map((b) => b.typeSettings.assignedType)),
    ];
    const allDevicePropertiesInStation = [
      ...new Set(
        this.station?.devices.flatMap((b) => b.typeSettings.assignedProperties)
      ),
    ];
    this.station.deviceTypeDescriptions = allDeviceTypesInStation.map(
      (deviceType) => {
        return {
          deviceType,
          description:
            this.station.deviceTypeDescriptions.find(
              (bt) => bt.deviceType === deviceType
            )?.description ?? "Eine Box nur für Dich",
        };
      }
    );
    this.station.devicePropertyDescriptions = allDevicePropertiesInStation.map(
      (property) => {
        return {
          property,
          description:
            this.station.devicePropertyDescriptions?.find(
              (bp) => bp.property === property
            )?.description ?? property,
        };
      }
    );
    const pricings: Pricing[] = [];
    for (const device of this.station.devices) {
      const pricing = getDefaultDevicePricing(
        device.typeSettings.assignedType,
        device.typeSettings.assignedProperties,
        this.hasFinanceData
      );
      const existing = pricings.find(
        (p) =>
          p.deviceType === device.typeSettings.assignedType &&
          JSON.stringify(p.deviceProperties) ===
            JSON.stringify(device.typeSettings.assignedProperties)
      );
      if (!existing) {
        pricings.push(pricing);
      }
    }
    this.station.pricingModels = pricings;
  }

  onLocationValid(valid: boolean): void {
    this.locationValid = valid;
  }

  onDeviceCreated(device: Device): void {
    this.station.devices.push(device);
  }

  onDeviceDeleted(deviceNumber: number): void {
    const index = this.station.devices.findIndex(
      (b) => b.deviceNumber === deviceNumber
    );
    if (index === -1) {
      throw new Error("tried to delete non-existing device");
    }
    this.station.devices.splice(index, 1);
  }

  onStationLocationSave(newLocation: StationLocation): void {
    if (!this.station.stationLocation) return;
    this.station.stationLocation = newLocation;
  }

  confirmLocation(): void {
    const locationForm: any = this.$refs.locationForm; // any is required to avoid linter error
    const result = locationForm.tryValidate();
    if (result) this.step += 1;
  }

  onDeviceHardwareSettingsSave(o: {
    deviceNumber: number;
    deviceHardwareSettings: DeviceHardwareSettings;
  }): void {
    const device = this.station?.devices.find(
      (b) => b.deviceNumber === o.deviceNumber
    );
    if (!device) {
      throw new Error("tried to edit hardware settings of non-existing device");
    }
    device.hardwareSettings = o.deviceHardwareSettings;
  }

  onRentalTimeSave(o: {
    deviceNumber: number;
    maxPrebookDuration: string;
    maxRentalDuration: string;
  }): void {
    const device = this.station?.devices.find(
      (b) => b.deviceNumber === o.deviceNumber
    );
    if (!device) {
      throw new Error("tried to edit hardware settings of non-existing device");
    }
    device.maxPrebookDuration = o.maxPrebookDuration;
    device.maxRentalDuration = o.maxRentalDuration;
  }

  onDeviceDimensionsSave(o: {
    deviceNumber: number;
    deviceDimensions: DeviceDimensions;
  }): void {
    const device = this.station?.devices.find(
      (b) => b.deviceNumber === o.deviceNumber
    );
    if (!device) {
      throw new Error("tried to edit hardware settings of non-existing device");
    }
    device.dimensions = o.deviceDimensions;
  }

  onDeviceDescriptionSave(o: {
    deviceNumber: number;
    descriptionText: string;
  }): void {
    const device = this.station?.devices.find(
      (b) => b.deviceNumber === o.deviceNumber
    );
    if (!device) {
      throw new Error("tried to edit hardware settings of non-existing device");
    }
    device.descriptionText = o.descriptionText;
  }

  onDeviceCapacitySave(o: { deviceNumber: number; capacity: number }): void {
    const device = this.station?.devices.find(
      (b) => b.deviceNumber === o.deviceNumber
    );
    if (!device) {
      throw new Error("tried to edit hardware settings of non-existing device");
    }
    device.capacity = o.capacity;
  }

  onRentalMethodSave(o: {
    deviceNumber: number;
    rentalMethod: RentalMethod;
  }): void {
    const device = this.station?.devices.find(
      (b) => b.deviceNumber === o.deviceNumber
    );
    if (!device) {
      throw new Error("tried to edit hardware settings of non-existing device");
    }
    device.rentalMethod = o.rentalMethod;
  }

  onDevicePropertiesSave(o: {
    deviceNumber: number;
    deviceTypeSettings: DeviceTypeSettings;
  }): void {
    const device = this.station?.devices.find(
      (b) => b.deviceNumber === o.deviceNumber
    );
    if (!device) {
      throw new Error("tried to edit hardware settings of non-existing device");
    }
    device.typeSettings = o.deviceTypeSettings;
  }

  onDeviceTypeDescriptionsValid(valid: boolean): void {
    this.descriptionsValid = valid;
  }

  onDeviceTypeDescriptionsSave(
    deviceTypeDescriptions: DeviceTypeDescription[]
  ): void {
    if (!this.station.deviceTypeDescriptions) return;
    this.station.deviceTypeDescriptions = deviceTypeDescriptions;
  }

  confirmTypeDescriptons(): void {
    const typeDescriptionForm: any = this.$refs.typeDescriptionForm; // any is required to avoid linter error
    if (typeDescriptionForm.tryValidate()) this.step += 1;
  }

  onDevicePropertyDescriptionsValid(valid: boolean): void {
    this.propertiesValid = valid;
  }

  onDevicePropertyDescriptionsSave(
    devicePropertyDescriptions: DevicePropertyDescription[]
  ): void {
    if (!this.station.devicePropertyDescriptions) return;
    this.station.devicePropertyDescriptions = devicePropertyDescriptions;
  }

  confirmDevicePropertyDescriptions(): void {
    const devicePropertyDescriptionForm: any =
      this.$refs.devicePropertyDescriptionForm; // any is required to avoid linter error
    if (devicePropertyDescriptionForm.tryValidate()) {
      this.step += 1;
    }
  }

  onStationPricingSave(pricing: Pricing): void {
    if (!this.station.pricingModels) return;
    const index = this.station.pricingModels.findIndex((p) => {
      return (
        p.deviceType === pricing.deviceType &&
        JSON.stringify(p.deviceProperties) ===
          JSON.stringify(pricing.deviceProperties)
      );
    });
    if (index !== -1) {
      this.station.pricingModels.splice(index, 1, pricing);
    }
  }
}
